// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * @file Minimal shared console logger.
 * @module generic/logger
 * @author Lari Natri
 * @license GPL-3.0-or-later
 * @version 0.0.5
 *
 * The logger is intentionally dependency-free and works in browsers and Node.
 * It adds level filtering and optional console-channel routing on top of the
 * built-in `console` API.
 */

/** Logging levels used by {@link Logger}. */
export enum LogLevel {
  TRACE = 0,
  DEBUG = 1,
  INFO = 2,
  WARN = 3,
  ERROR = 4,
  FATAL = 5,
}

/** Small shared console logger with configurable minimum and default levels. */
export class Logger {
  static #defaultLevel: LogLevel = LogLevel.INFO;
  static #minLevel: LogLevel = LogLevel.TRACE;

  public static enabled = true;
  public static useConsoleChannels = true;

  static #logLevelIsValid(level: LogLevel): boolean {
    if (typeof level === "undefined") {
      Logger.warn("logLevelIsValid: level is undefined");
      return false;
    }
    if (level < LogLevel.TRACE || level > LogLevel.FATAL) {
      Logger.warn(`logLevelIsValid: level is out of range: ${level}`);
      return false;
    }
    return true;
  }

  public static getMinLevel(): LogLevel {
    return this.#minLevel;
  }

  public static setMinLevel(level: LogLevel): LogLevel {
    if (Logger.#logLevelIsValid(level)) this.#minLevel = level;
    else Logger.error("setMinLevel: level is invalid");
    return this.#minLevel;
  }

  public static getDefaultLevel(): LogLevel {
    return this.#defaultLevel;
  }

  public static setDefaultLevel(level: LogLevel): LogLevel {
    if (Logger.#logLevelIsValid(level)) this.#defaultLevel = level;
    else Logger.error("setDefaultLevel: level is invalid");
    return this.#defaultLevel;
  }

  public static logWithLevelParam(level: LogLevel, ...msgs: unknown[]): void {
    if (!Logger.enabled) return;
    if (!Logger.#logLevelIsValid(level)) level = LogLevel.INFO;
    if (level < Logger.#minLevel) return;

    const padding = LogLevel[level].length < 5 ? " " : "";
    const preMsg = `[${LogLevel[level]}]${padding}`;

    if (!Logger.useConsoleChannels) {
      console.log(preMsg, ...msgs);
      return;
    }

    switch (level) {
      case LogLevel.TRACE:
      case LogLevel.DEBUG:
        console.debug(preMsg, ...msgs);
        break;
      case LogLevel.INFO:
        console.info(preMsg, ...msgs);
        break;
      case LogLevel.WARN:
        console.warn(preMsg, ...msgs);
        break;
      case LogLevel.ERROR:
      case LogLevel.FATAL:
        console.error(preMsg, ...msgs);
        break;
      default:
        console.log(preMsg, ...msgs);
    }
  }

  public static trace(...msgs: unknown[]): void {
    Logger.logWithLevelParam(LogLevel.TRACE, ...msgs);
  }
  public static debug(...msgs: unknown[]): void {
    Logger.logWithLevelParam(LogLevel.DEBUG, ...msgs);
  }
  public static info(...msgs: unknown[]): void {
    Logger.logWithLevelParam(LogLevel.INFO, ...msgs);
  }
  public static warn(...msgs: unknown[]): void {
    Logger.logWithLevelParam(LogLevel.WARN, ...msgs);
  }
  public static error(...msgs: unknown[]): void {
    Logger.logWithLevelParam(LogLevel.ERROR, ...msgs);
  }
  public static fatal(...msgs: unknown[]): void {
    Logger.logWithLevelParam(LogLevel.FATAL, ...msgs);
  }
  public static log(...msgs: unknown[]): void {
    Logger.logWithLevelParam(Logger.#defaultLevel, ...msgs);
  }
}
